import type { MedplumClient } from '@medplum/core';
import type { Patient, Practitioner, Task } from '@medplum/fhirtypes';

import {
  CLINICAL_TASK_CATEGORY_SYSTEM,
  CLINICAL_TASK_ID_SYSTEM,
  type ClinicalTaskCategory,
  type ClinicalTaskPriority,
  readTaskCategory,
  readTaskDueDate,
  readTaskPriority,
  readTaskTitle,
} from '@eluve/clinical-tasks';
import type { ExtendedSortingState } from '@eluve/components';
import { formatHumanName } from '@eluve/fhir-renderer';
import { DERIVED_FROM_APPOINTMENT_SYSTEM } from '@eluve/mini-apps';

/** Statuses the table exposes as filter options — the clinician-visible
 *  lifecycle plus tasks a clinician cancelled. The rejected-draft tombstone
 *  (`cancelled` + intent `proposal`) is excluded server-side by the query's
 *  `_filter`, not by this list. */
export const TABLE_TASK_STATUSES = [
  'draft',
  'requested',
  'in-progress',
  'completed',
  'cancelled',
] as const;
export type TableTaskStatus = (typeof TABLE_TASK_STATUSES)[number];

/** Default rows per page; the URL `perPage` param overrides it. Must be one of
 *  the pagination control's options ([10, 20, 30, 40, 50]) or its size selector
 *  renders blank. */
export const DEFAULT_TASK_PAGE_SIZE = 10;
/** `_id` batch size when resolving Patient/Practitioner display names. */
const NAME_RESOLVE_BATCH = 100;

// Reads must see review decisions and extractions that landed seconds ago
// (MedplumClient caches searches in-memory for 60s in the browser).
const NO_CACHE = { cache: 'no-cache' } as const;

export interface ClinicalTaskRow extends Record<string, unknown> {
  id: string;
  description: string;
  category: ClinicalTaskCategory | null;
  priority: ClinicalTaskPriority | null;
  dueDate: string | null;
  status: TableTaskStatus;
  /** Sort key: authoredOn, falling back to meta.lastUpdated. */
  createdAt: string | null;
  /** null = unreadable under the viewer's access policy (render "Restricted"). */
  patientName: string | null;
  ownerReference: string | null;
  ownerName: string | null;
  requesterName: string | null;
  appointmentId: string | null;
  /** null = appointment unresolvable for this viewer (no sub-line, no link). */
  appointmentName: string | null;
  /** The full resource — the detail sheet edits it browser-direct (non-draft
   *  statuses) by PUTting a modified copy, mirroring the panel's Done flip. */
  raw: Task;
}

export type TaskViewerScope =
  | { level: 'broad' }
  | { level: 'scoped'; practitionerReference: string };

/** One page of the table's filter/sort/pagination state, in the terms the
 *  Medplum search is built from. `filters` is keyed by table column id. */
export interface TaskTableQuery {
  scope: TaskViewerScope;
  offset: number;
  limit: number;
  sort: ExtendedSortingState<ClinicalTaskRow>;
  filters: Record<string, string[] | null | undefined>;
  /** Free-text keyword; matched against the task title (code.text) via FHIR
   *  token `code:text` (case-insensitive substring). */
  search?: string | null;
}

export interface ClinicalTaskPage {
  tasks: Task[];
  /** Server-reported match count (`Bundle.total`) — drives pagination. */
  total: number;
}

/** Sortable columns → their FHIR search param. Columns absent here can't sort
 *  server-side (no indexed param): Task/description; Assigned To / Created By
 *  (a reference param sorts by opaque id, not by name); and Planned — the due
 *  date lives in `Task.restriction.period`, which no Task search param covers
 *  (`period` indexes the unrelated `executionPeriod`). */
const SORT_PARAM_BY_COLUMN: Partial<Record<keyof ClinicalTaskRow, string>> = {
  status: 'status',
  priority: 'priority',
};

/** Newest-first when the user hasn't picked a sort. `_lastUpdated` is always
 *  present, unlike `authored-on`. */
const DEFAULT_SORT = '-_lastUpdated';

const buildSort = (sort: ExtendedSortingState<ClinicalTaskRow>): string => {
  const parts = sort
    .map(({ id, desc }) => {
      const param = SORT_PARAM_BY_COLUMN[id];
      return param ? `${desc ? '-' : ''}${param}` : null;
    })
    .filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(',') : DEFAULT_SORT;
};

/**
 * The one `_filter` expression, built from the viewer scope:
 * - Always drops the rejected-draft tombstone (`cancelled` + `proposal`) —
 *   `not (cancelled and proposal)`, expanded via De Morgan so it reads as a
 *   plain OR. A real cancelled task (`cancelled` + `order`) is kept.
 * - Scoped viewers add the owner∪requester union (FHIR can't OR two params
 *   any other way), collapsing what used to be two searches into one
 *   stably-paginated query.
 */
const buildFilter = (scope: TaskViewerScope): string => {
  const notTombstone = '(status ne cancelled or intent ne proposal)';
  if (scope.level === 'broad') return notTombstone;
  const ref = scope.practitionerReference;
  return `((owner eq ${ref} or requester eq ${ref}) and ${notTombstone})`;
};

const applyFacetFilters = (
  params: Record<string, string | number>,
  filters: TaskTableQuery['filters'],
): void => {
  const status = filters.status;
  if (status?.length) params.status = status.join(',');
  const category = filters.category;
  if (category?.length) {
    params.code = category
      .map((c) => `${CLINICAL_TASK_CATEGORY_SYSTEM}|${c}`)
      .join(',');
  }
  const priority = filters.priority;
  if (priority?.length) params.priority = priority.join(',');
  // Assignee values are already full `Practitioner/<id>` references.
  const owner = filters.ownerReference;
  if (owner?.length) params.owner = owner.join(',');
};

/**
 * Translate the table state into Medplum FHIR search params. Pure and
 * exported so the mapping (facets → params, scope → `_filter`, sort → `_sort`)
 * is unit-tested without a client. Multi-select facets rely on FHIR's
 * comma-OR semantics within one param.
 */
export function buildTaskSearchParams(
  query: TaskTableQuery,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    identifier: `${CLINICAL_TASK_ID_SYSTEM}|`,
    _total: 'accurate',
    _count: query.limit,
    _offset: query.offset,
    _sort: buildSort(query.sort),
    _filter: buildFilter(query.scope),
  };
  applyFacetFilters(params, query.filters);
  // Keyword search over the task title. `code.text` (and coding.display) index
  // into the token text column, so `code:text` is a case-insensitive substring
  // match — the only server-side free-text search Task exposes on our Medplum.
  const search = query.search?.trim();
  if (search) params['code:text'] = search;
  return params;
}

/**
 * Fetch one page of clinical Tasks. Filtering, sorting, the owner∪requester
 * union, and tombstone exclusion all run server-side; `Bundle.total` is the
 * unfiltered-by-client match count that drives pagination.
 */
export async function fetchClinicalTasksPage(
  client: MedplumClient,
  query: TaskTableQuery,
): Promise<ClinicalTaskPage> {
  const bundle = await client.search(
    'Task',
    buildTaskSearchParams(query),
    NO_CACHE,
  );
  const tasks = (bundle.entry ?? []).flatMap((entry) =>
    entry.resource?.resourceType === 'Task' ? [entry.resource] : [],
  );
  return { tasks, total: bundle.total ?? tasks.length };
}

const referenceId = (
  reference: string | undefined,
  resourceType: 'Patient' | 'Practitioner',
): string | null => {
  const prefix = `${resourceType}/`;
  return reference?.startsWith(prefix) ? reference.slice(prefix.length) : null;
};

export const readTaskAppointmentId = (task: Task): string | null =>
  task.identifier?.find((i) => i.system === DERIVED_FROM_APPOINTMENT_SYSTEM)
    ?.value ?? null;

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/**
 * Resolve display names for a set of resource ids via `_id` batch searches.
 * Results the viewer's access policy filters out are simply absent — callers
 * treat a missing entry as "restricted", never as an error.
 */
async function resolveNames(
  client: MedplumClient,
  resourceType: 'Patient' | 'Practitioner',
  ids: string[],
): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  for (const batch of chunk(ids, NAME_RESOLVE_BATCH)) {
    const found = await client.searchResources(
      resourceType,
      { _id: batch.join(','), _count: batch.length },
      NO_CACHE,
    );
    for (const resource of found as (Patient | Practitioner)[]) {
      if (!resource.id) continue;
      const display = formatHumanName(resource.name?.[0]);
      if (display) names.set(resource.id, display);
    }
  }
  return names;
}

/**
 * Project one fetched page of Tasks into table rows, resolving Patient and
 * Practitioner names browser-direct. Server order is preserved (no client
 * re-sort). Appointment names are resolved separately (Hasura) and merged by
 * the caller via `appointmentNames`.
 */
export async function buildClinicalTaskRows(
  client: MedplumClient,
  tasks: Task[],
  appointmentNames: ReadonlyMap<string, string>,
): Promise<ClinicalTaskRow[]> {
  const patientIds = new Set<string>();
  const practitionerIds = new Set<string>();
  for (const task of tasks) {
    const patientId = referenceId(task.for?.reference, 'Patient');
    if (patientId) patientIds.add(patientId);
    for (const ref of [task.owner?.reference, task.requester?.reference]) {
      const practitionerId = referenceId(ref, 'Practitioner');
      if (practitionerId) practitionerIds.add(practitionerId);
    }
  }
  const [patientNames, practitionerNames] = await Promise.all([
    resolveNames(client, 'Patient', [...patientIds]),
    resolveNames(client, 'Practitioner', [...practitionerIds]),
  ]);

  const practitionerName = (reference: string | undefined): string | null => {
    const id = referenceId(reference, 'Practitioner');
    return (id && practitionerNames.get(id)) ?? null;
  };

  return tasks
    .filter((t): t is Task & { id: string } => Boolean(t.id))
    .map((task) => {
      const patientId = referenceId(task.for?.reference, 'Patient');
      const appointmentId = readTaskAppointmentId(task);
      return {
        id: task.id,
        description: readTaskTitle(task) ?? '(no description)',
        category: readTaskCategory(task),
        priority: readTaskPriority(task),
        dueDate: readTaskDueDate(task),
        status: (TABLE_TASK_STATUSES as readonly string[]).includes(task.status)
          ? (task.status as TableTaskStatus)
          : 'draft',
        createdAt: task.authoredOn ?? task.meta?.lastUpdated ?? null,
        patientName: (patientId && patientNames.get(patientId)) ?? null,
        ownerReference: task.owner?.reference ?? null,
        ownerName: practitionerName(task.owner?.reference),
        requesterName: practitionerName(task.requester?.reference),
        appointmentId,
        appointmentName:
          (appointmentId && appointmentNames.get(appointmentId)) ?? null,
        raw: task,
      };
    });
}
